import { styled } from '@mui/material'
import Page from 'components/DocumentDetail/Viewer/Page'
import { Document } from 'entities/Documents'
import { useRef, useState, useEffect, useCallback, useMemo } from 'react'

const Container = styled('div')({
  display: 'flex',
  flexShrink: 0,
  flex: 1,
  overflow: 'auto',
  userSelect: 'none',
})

const Content = styled('div')(({ theme }) => ({
  boxSizing: 'content-box',
  display: 'flex',
  flexShrink: 0,
  alignItems: 'center',
  justifyContent: 'center',
  margin: 'auto',
  padding: theme.spacing(5),
}))

const Scaleable = styled('div')({
  flexShrink: 0,
  fontSize: 0,
  transformOrigin: 'center center',
})

/*
const Loading = styled('div')({
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
})
*/

const VIEWER_SIDE_PADDING = 40

type Props = {
  onLoad: () => void
  userScale: number
  detail: Document
}

const Pages = ({ onLoad, userScale, detail }: Props) => {
  const srcs = detail.getSrcs()
  const highlights = useMemo(() => detail.getHighlights(), [detail])
  const [loading, setLoading] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const [contentSize, setContentSize] = useState({ width: 0, height: 0 })
  const [fitScale, setFitScale] = useState(1)
  const containerRef = useRef<HTMLDivElement>(null)
  const ocrServiceId = detail.docInfo.ocrService.ocrServiceId

  // load image onMounted
  useEffect(() => {
    if (loading) return

    setLoading(true)
    Promise.all(
      srcs.map(
        (src) =>
          new Promise<HTMLImageElement>((resolve /*, reject */) => {
            const img = document.createElement('img')
            img.onload = () => resolve(img)
            img.src = src
          }),
      ),
    ).then((imgs) => {
      const maxWidth = Math.max.apply(
        this,
        imgs.map((img) => img?.width),
      )
      const totalHeight = imgs.reduce(
        (h, img) => h + img?.height + VIEWER_SIDE_PADDING,
        -VIEWER_SIDE_PADDING,
      ) // add padding between images
      setContentSize({
        width: maxWidth,
        height: totalHeight,
      })
      onLoad()
    })
  }, [loading, srcs, onLoad])

  useEffect(() => {
    if (containerRef.current?.offsetWidth && contentSize.width && !initialized) {
      setFitScale((containerRef.current.offsetWidth - VIEWER_SIDE_PADDING * 2) / contentSize.width)
      setInitialized(true)
    }
  }, [contentSize, containerRef, setFitScale, initialized, setInitialized])

  const getHighlightsByPage = useCallback(
    (page) => {
      return highlights.filter((data) => data.detectedPage === page)
    },
    [highlights],
  )

  const scale = userScale * fitScale

  return (
    <Container ref={containerRef}>
      <Content
        style={{
          width: contentSize.width * scale,
          height: contentSize.height * scale,
        }}
      >
        <Scaleable style={{ transform: `scale(${scale})`, ...contentSize }}>
          {srcs.map((src, idx) => (
            <Page
              key={src}
              src={src}
              highlights={getHighlightsByPage(idx)}
              ocrServiceId={ocrServiceId}
            />
          ))}
        </Scaleable>
      </Content>
    </Container>
  )
}

export default Pages
